import { RefObject, useEffect } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";

import { TableRow } from "./DataTable";
import { TableRowElement } from "./TableRowElement";

export interface VirtualizedRowsProps<IDType extends number | string> {
  rows: TableRow<IDType>[];
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  estimatedRowHeightPx: number;
  isBackgroundFetching?: boolean;
  expandedRowRenderer?: (tableRow: TableRow<IDType>) => React.ReactNode;
  lockedTabKey: string | null | undefined;
  registerTabInputRef: (rowId: IDType, tabLockKey: string, el: HTMLElement | null) => void;
  focusNextLockedCell: (rowId: IDType, tabLockKey: string) => void;
  onDraftStateChange: (rowId: IDType, hasDrafts: boolean) => void;
  /** Filled in with a scroller so the parent can bring an off-screen row into view before focusing it. */
  scrollToIndexRef: RefObject<((index: number) => void) | null>;
}

/**
 * Windowed row list. This lives apart from DataTable because `useVirtualizer` returns functions
 * React Compiler cannot memoize, which makes it skip the whole component it is called in. Keeping
 * the call here means only this small component opts out, and DataTable itself still compiles.
 */
export const VirtualizedRows = <IDType extends number | string>({
  rows,
  scrollContainerRef,
  estimatedRowHeightPx,
  isBackgroundFetching,
  expandedRowRenderer,
  lockedTabKey,
  registerTabInputRef,
  focusNextLockedCell,
  onDraftStateChange,
  scrollToIndexRef,
}: VirtualizedRowsProps<IDType>) => {
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => estimatedRowHeightPx,
    overscan: 8,
    getItemKey: (index) => rows[index]?.id ?? index,
  });

  useEffect(() => {
    scrollToIndexRef.current = (index: number) => virtualizer.scrollToIndex(index, { align: "center" });
    return () => {
      scrollToIndexRef.current = null;
    };
  }, [virtualizer, scrollToIndexRef]);

  return (
    <div
      className={`transition-opacity ease-in-out ${isBackgroundFetching ? "opacity-80 pointer-events-none" : ""}`}
      style={{
        height: virtualizer.getTotalSize(),
        position: "relative",
        width: "100%",
      }}
    >
      {virtualizer.getVirtualItems().map((virtualItem) => {
        const row = rows[virtualItem.index];
        return (
          <div
            key={row.id}
            data-index={virtualItem.index}
            ref={virtualizer.measureElement}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              transform: `translateY(${virtualItem.start}px)`,
            }}
          >
            <TableRowElement<IDType>
              tableRow={row}
              isLoading={false}
              showDetailsColumnHeadCell={!!expandedRowRenderer}
              expandedRowRenderer={expandedRowRenderer}
              rowTailwindClasses={row.rowTailwindClasses}
              rowContainerTailwindClasses={row.rowContainerTailwindClasses}
              lockedTabKey={lockedTabKey}
              registerTabInputRef={registerTabInputRef}
              focusNextLockedCell={focusNextLockedCell}
              onDraftStateChange={onDraftStateChange}
              disableMountAnimation
            />
          </div>
        );
      })}
    </div>
  );
};

export default VirtualizedRows;
